import React, { useState } from 'react';
import { Button, SparkIcon, TagColored } from '@spark';
import SampleTextBlockCard from '@/features/tasks/components/SampleTextBlockCard';
import ReferenceResponseCard from '@/features/tasks/components/ReferenceResponseCard';
import { TaskType, getGenerativeOutputNoun } from '@/shared/domain/taskTypes';
import { isMissingGenerativeOutput } from './summarizationUtils';

// Cap shared by Original Source, Generated, and Reference so the three
// columns use the same body max — max-height only, cards still hug short text.
const SAMPLE_BODY_MAX_HEIGHT = 220;

const CollapsibleSummaryCard = ({ title, subtitle, text, defaultOpen = true }) => {
  const missingOutput = isMissingGenerativeOutput(text);
  return (
    <SampleTextBlockCard
      title={title}
      subtitle={subtitle}
      text={text}
      maxHeight={SAMPLE_BODY_MAX_HEIGHT}
      collapsible
      defaultOpen={defaultOpen}
      hideMetaCounts={missingOutput}
    >
      {missingOutput ? <TagColored label="No Response" variant="disabled" /> : undefined}
    </SampleTextBlockCard>
  );
};

const SummarizationSampleDetail = ({
  sample,
  taskType = TaskType.summarization,
  comparisonModelLabel,
  studentModelLabel,
  onBack,
}) => {
  const [showReferenceSummary, setShowReferenceSummary] = useState(false);
  const isContentGeneration = taskType === TaskType.contentGeneration;
  const outputNoun = getGenerativeOutputNoun(taskType);
  const outputNounTitle = outputNoun.charAt(0).toUpperCase() + outputNoun.slice(1);

  // Reference Summary content is now rendered by the shared
  // ``ReferenceResponseCard`` (so the Playground detail and this page stay
  // in sync). The card returns ``null`` when there is no test response, so
  // we mirror that here to suppress the toggle button entirely.
  const referenceGroundTruth =
    sample?.reference_summary ?? sample?.test_response ?? '';
  const hasReferenceSummary =
    typeof referenceGroundTruth === 'string'
      ? referenceGroundTruth.trim().length > 0
      : !!referenceGroundTruth;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Button
          variant="link"
          startIcon={<SparkIcon slug="chevron-left" />}
          onClick={onBack}
          style={{ paddingInline: 0 }}
        >
          Back to All Samples
        </Button>
        {hasReferenceSummary && (
          <Button
            variant="link"
            size="small"
            style={{ fontSize: 12, padding: 0 }}
            onClick={() => setShowReferenceSummary((v) => !v)}
          >
            {showReferenceSummary
              ? `Hide Reference ${outputNounTitle}`
              : `View Reference ${outputNounTitle}`}
          </Button>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: showReferenceSummary
            ? 'minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr)'
            : 'minmax(0, 1fr) minmax(0, 1.4fr)',
          gap: 16,
          alignItems: 'start',
          alignContent: 'start',
          width: '100%',
          minWidth: 0,
        }}
      >
        <SampleTextBlockCard
          title="Original Source Text"
          subtitle={
            isContentGeneration
              ? 'Original input the model responds to'
              : 'Original document to be summarized'
          }
          text={sample?.original_source_text}
          maxHeight={SAMPLE_BODY_MAX_HEIGHT}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {comparisonModelLabel && (
            <CollapsibleSummaryCard
              title={`Generated ${outputNoun} - ${comparisonModelLabel}`}
              subtitle={`${outputNoun} generated by the ${comparisonModelLabel} model`}
              text={sample?.teacher_summary}
              defaultOpen={true}
            />
          )}
          <CollapsibleSummaryCard
            title={`Generated ${outputNoun} - ${studentModelLabel || 'Specific AI'}`}
            subtitle={`${outputNoun} generated by the ${studentModelLabel || 'Specific AI'} model`}
            text={sample?.student_summary}
            defaultOpen={true}
          />
        </div>

        {showReferenceSummary && (
          <ReferenceResponseCard
            taskType={taskType}
            groundTruth={referenceGroundTruth}
            title={`Reference ${outputNounTitle}`}
            maxHeight={SAMPLE_BODY_MAX_HEIGHT}
          />
        )}
      </div>
    </div>
  );
};

export default SummarizationSampleDetail;

